import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  collection,
  // addDoc,
  getDocs,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "./firebase";
import { useAuth } from "./AuthContext";
import { useToast } from "./ToastContext";
import ScrollToTopButton from "./components/ScrollToTop";
import Technique from "./components/TechniqueCard";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ConfirmModal from "./components/ConfirmModal";

function Collections() {
  const user = useAuth();
  const { addToast } = useToast();
  const [collections, setCollections] = useState([]);
  const [techniques, setTechniques] = useState([]);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmDeleteCollection, setConfirmDeleteCollection] = useState(null);
  const pendingCollectionDeletes = useRef({});
  // const [newCollectionName, setNewCollectionName] = useState("");

  const fetchData = useCallback(async () => {
    if (!user) return;

    try {
      const snapshot = await getDocs(
        collection(db, "users", user.uid, "collections")
      );
      const collectionsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCollections(collectionsData);

      const [kyohonResponse, patreonResponse] = await Promise.all([
        fetch("/kyohon.json"),
        fetch("/patreon.json"),
      ]);
      const kyohonData = await kyohonResponse.json();
      const patreonRaw = await patreonResponse.json();

      const patreonData = Array.isArray(patreonRaw)
        ? patreonRaw.map((item, index) => {
            const videoUrl = item.video_url || "";
            const pageUrl = item.page_url || "";
            const vimeoId = (() => {
              const m = String(videoUrl).match(/vimeo\.com\/(\d+)/i);
              return m ? m[1] : null;
            })();
            const seriesValues = Array.isArray(item.series)
              ? item.series
              : item.series
              ? [item.series]
              : [];
            const tags = Array.isArray(item.tags) ? item.tags : [];
            return {
              name: item.title || `Patreon item ${index + 1}`,
              author: item.author || "Hua Hua",
              platform: "Patreon",
              description: item.description || item.excerpt || "",
              url: pageUrl || videoUrl || `patreon-${index + 1}`,
              previewUrl: videoUrl || pageUrl || "",
              linkLabel: pageUrl ? "Patreon Post" : "Patreon",
              secondaryUrl: pageUrl && videoUrl ? videoUrl : "",
              secondaryLabel: "Watch Video",
              videoUrl,
              thumbnail: vimeoId ? `https://vumbnail.com/${vimeoId}.jpg` : null,
              date: item.date || "",
              tags: [...new Set([...seriesValues, ...tags])],
              series: seriesValues,
            };
          })
        : [];

      setTechniques([...kyohonData, ...patreonData]);
    } catch (error) {
      console.error("Error fetching collections or techniques:", error);
      addToast("Failed to load collections", "error");
    } finally {
      setLoading(false);
    }
  }, [user, addToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // const createCollection = async () => {
  //   if (!user || !newCollectionName.trim()) return;

  //   try {
  //     await addDoc(collection(db, "users", user.uid, "collections"), {
  //       name: newCollectionName.trim(),
  //       description: "",
  //       tutorials: [],
  //     });
  //     setNewCollectionName("");
  //     fetchData();
  //   } catch (error) {
  //     console.error("Error creating collection:", error);
  //   }
  // };

  const undoDeleteCollection = useCallback((id) => {
    const pending = pendingCollectionDeletes.current[id];
    if (!pending) return;

    clearTimeout(pending.timeoutId);
    setCollections((prev) => {
      const exists = prev.some((collection) => collection.id === pending.collection.id);
      if (exists) return prev;
      const next = [...prev];
      next.splice(pending.index, 0, pending.collection);
      return next;
    });
    delete pendingCollectionDeletes.current[id];
    addToast("Collection restored", "info");
  }, [addToast]);

  const scheduleDeleteCollection = useCallback((collectionToDelete) => {
    if (!user || !collectionToDelete) return;

    const originalIndex = collections.findIndex((c) => c.id === collectionToDelete.id);
    if (originalIndex === -1) return;

    setCollections((prev) => prev.filter((c) => c.id !== collectionToDelete.id));

    const timeoutId = setTimeout(async () => {
      try {
        await deleteDoc(doc(db, "users", user.uid, "collections", collectionToDelete.id));
        addToast(`Deleted "${collectionToDelete.name}"`, "success");
      } catch (err) {
        console.error("Failed to delete collection", err);
        setCollections((prev) => {
          const exists = prev.some((collection) => collection.id === collectionToDelete.id);
          if (exists) return prev;
          const next = [...prev];
          next.splice(originalIndex, 0, collectionToDelete);
          return next;
        });
        addToast("Failed to delete collection", "error");
      } finally {
        delete pendingCollectionDeletes.current[collectionToDelete.id];
      }
    }, 5000);

    pendingCollectionDeletes.current[collectionToDelete.id] = {
      timeoutId,
      collection: collectionToDelete,
      index: originalIndex,
    };

    addToast(
      `Deleting "${collectionToDelete.name}" in 5 seconds`,
      "warning",
      5000,
      {
        actionLabel: "Undo",
        onAction: () => undoDeleteCollection(collectionToDelete.id),
      }
    );
  }, [addToast, collections, undoDeleteCollection, user]);

  useEffect(() => {
    const pendingDeletesAtUnmount = pendingCollectionDeletes.current;
    return () => {
      Object.values(pendingDeletesAtUnmount).forEach((pending) => {
        clearTimeout(pending.timeoutId);
      });
    };
  }, []);

  const confirmDeleteCollectionNow = () => {
    if (!confirmDeleteCollection) return;
    scheduleDeleteCollection(confirmDeleteCollection);
    setConfirmDeleteCollection(null);
  };

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setShowScrollButton(true);
      } else {
        setShowScrollButton(false);
      }
    };

    window.addEventListener("scroll", toggleVisibility);
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);

  const getTechniquesForCollection = (collection) => {
    if (!collection?.tutorials) return [];
    return techniques.filter((t) => collection.tutorials.includes(t.url));
  };

  if (!user) return <p>Please log in to view your collections.</p>;

  if (loading) return <div>Loading collections...</div>;

  return (
    <>
      <Header />
      <div className="app-container">
        <h1>Your Collections</h1>

        <div className="collection-list">
          {collections.map((collection) => (
            <button
              key={collection.id}
              className="collection-button"
              onClick={() => {
                const element = document.getElementById(collection.name);
                if (element) {
                  element.scrollIntoView({ behavior: "smooth" });
                }
              }}
            >
              {collection.name}
            </button>
          ))}
        </div>

        <div className="collection-navigation"></div>

        {collections.length > 0 ? (
          collections.map((collection) => {
            const relatedTechniques = getTechniquesForCollection(collection);

            return (
              <div
                key={collection.id}
                id={collection.name}
                className="collection-item"
              >
                <div className="collection-header">
                  <h2 className="collection-title">{collection.name}</h2>
                </div>

                {relatedTechniques.length > 0 ? (
                  <div className="technique-list">
                    {relatedTechniques.map((technique) => (
                      <Technique
                        key={technique.url}
                        technique={technique}
                        isExpanded={false}
                        onExpand={() => {}}
                      />
                    ))}
                  </div>
                ) : (
                  <p>No techniques in this collection yet.</p>
                )}
                <div className="collection-actions">
                  <button
                    className="rename-collection-button"
                    onClick={async () => {
                      const nameInput = window.prompt(
                        "Enter a new name for this collection",
                        collection.name
                      );
                      if (nameInput === null) return;
                      const newName = nameInput.trim();
                      if (!newName || newName === collection.name) return;

                      try {
                        const { updateDoc } = await import(
                          "firebase/firestore"
                        );
                        await updateDoc(
                          doc(
                            db,
                            "users",
                            user.uid,
                            "collections",
                            collection.id
                          ),
                          { name: newName }
                        );
                        addToast(`Renamed to "${newName}"`, "success");
                        fetchData();
                      } catch (err) {
                        console.error("Failed to rename collection", err);
                        addToast("Failed to rename collection", "error");
                      }
                    }}
                  >
                    Rename this collection
                  </button>
                  <button
                    className="delete-collection-button"
                    onClick={() => setConfirmDeleteCollection(collection)}
                  >
                    Delete this collection
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="empty-state-block">
            <p>No collections yet.</p>
            <p className="empty-state-hint">
              Open any tutorial and use Add to collection to create your first one.
            </p>
          </div>
        )}
      </div>
      <ConfirmModal
        open={Boolean(confirmDeleteCollection)}
        title="Delete collection?"
        message={
          confirmDeleteCollection
            ? `"${confirmDeleteCollection.name}" will be deleted in 5 seconds. You can still undo from the toast.`
            : ""
        }
        confirmLabel="Delete collection"
        cancelLabel="Keep collection"
        destructive
        onConfirm={confirmDeleteCollectionNow}
        onCancel={() => setConfirmDeleteCollection(null)}
      />
      {showScrollButton && <ScrollToTopButton />}
      <Footer />
    </>
  );
}

export default Collections;
